/**
 * @file useTaskDetail.ts
 * @description Состояние окна задачи (phase19.31, crm.md T3–T4; контракт §13):
 * текущая версия задачи, перечитывание с сервера и действия — выполнить,
 * вернуть в работу, назначить, удалить. После любого действия (и после
 * отказа «задачу изменили одновременно») задача перечитывается, а странице
 * сообщается `onChanged`, чтобы она перечитала список.
 */

import { useCallback, useRef, useState } from "react";

import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import {
  completeActivity,
  deleteActivity,
  getActivity,
  reopenActivity,
  updateActivity,
  type ActivityDto,
} from "../../../lib/api-crm";
import { TASK_ACTION_TEXT, runTaskMutation } from "./task-mutations";

export type TaskAction = "complete" | "reopen" | "assign" | "delete";

export interface TaskDetailState {
  task: ActivityDto;
  busy: TaskAction | null;
  complete: () => void;
  reopen: () => void;
  assign: (userId: string | null) => void;
  remove: () => void;
}

export function useTaskDetail(initial: ActivityDto, onChanged: () => void, onClose: () => void): TaskDetailState {
  const notify = useNotify();
  const [task, setTask] = useState(initial);
  const [busy, setBusy] = useState<TaskAction | null>(null);

  /** Перечитать задачу и список; задачу удалили — закрыть окно. */
  const reload = useCallback(() => {
    onChanged();
    getActivity(task.id)
      .then(setTask)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) onClose();
      });
  }, [task.id, onChanged, onClose]);

  /** После удаления перечитывать нечего: список обновить, окно закрыть. */
  const afterDelete = useCallback(() => {
    onChanged();
    onClose();
  }, [onChanged, onClose]);

  // Одно действие за раз: второе нажатие, пока первое не ответило, игнорируется.
  const running = useRef(false);
  const run = useCallback(
    async (action: TaskAction, call: () => Promise<unknown>, success: string, fallback: string, after = reload) => {
      if (running.current) return;
      running.current = true;
      setBusy(action);
      try {
        await runTaskMutation(call, { success, fallback }, { notify, reload: after });
      } finally {
        running.current = false;
        setBusy(null);
      }
    },
    [notify, reload],
  );

  return {
    task,
    busy,
    complete: () =>
      void run("complete", () => completeActivity(task.id), TASK_ACTION_TEXT.completed, TASK_ACTION_TEXT.completeFailed),
    reopen: () => void run("reopen", () => reopenActivity(task.id), TASK_ACTION_TEXT.reopened, TASK_ACTION_TEXT.reopenFailed),
    assign: (userId) =>
      void run(
        "assign",
        () => updateActivity(task.id, { assigneeUserId: userId }),
        userId ? "Исполнитель назначен" : "Исполнитель снят",
        "Не удалось сменить исполнителя",
      ),
    remove: () => void run("delete", () => deleteActivity(task.id), "Задача удалена", "Не удалось удалить задачу", afterDelete),
  };
}
